/**
 * Image Style Selector Constants
 *
 * Defines available artistic styles for image generation.
 * Each style includes a descriptive prompt that is prepended to the user's input.
 */

/**
 * Available image styles with their configuration
 * Each style includes:
 * - nameKey: Translation key for the style name
 * - value: Unique identifier for the style
 * - imageUrl: Path to preview thumbnail
 * - prompt: Descriptive prompt that defines the artistic style
 */
export const IMAGE_STYLES = [
  {
    nameKey: "imageStyles.cyberpunk",
    value: "cyberpunk",
    imageUrl: "/assets/kitties/cyberpunk.webp",
    prompt:
      "Create an image in a cyberpunk aesthetic: vivid neon accents, futuristic textures, glowing details, and high-contrast lighting.",
  },
  {
    nameKey: "imageStyles.anime",
    value: "anime",
    imageUrl: "/assets/kitties/anime.webp",
    prompt:
      "Create an image in a detailed anime aesthetic: expressive eyes, smooth cel-shaded coloring, and clean linework. Emphasize emotion and character presence, with a sense of motion or atmosphere typical of anime scenes.",
  },
  {
    nameKey: "imageStyles.dramaticHeadshot",
    value: "headshot",
    imageUrl: "/assets/kitties/headshot.webp",
    prompt:
      "Create an ultra-realistic high-contrast black-and-white headshot, close up, black shadow background, 35mm lens, 4K quality, aspect ratio 4:3.",
  },
  {
    nameKey: "imageStyles.coloringBook",
    value: "coloring-book",
    imageUrl: "/assets/kitties/coloring-book.webp",
    prompt:
      "Create an image in a children's coloring book style: bold, even black outlines on white, no shading or tone. Simplify textures into playful, easily recognizable shapes.",
  },
  {
    nameKey: "imageStyles.photoShoot",
    value: "photoshoot",
    imageUrl: "/assets/kitties/photoshoot.webp",
    prompt: "Create an ultra-realistic professional photo shoot with soft lighting.",
  },
  {
    nameKey: "imageStyles.retroCartoon",
    value: "retro",
    imageUrl: "/assets/kitties/retro.webp",
    prompt:
      "Create a retro 1950s cartoon style image, minimal vector art, Art Deco inspired, clean flat colors, geometric shapes, mid-century modern design, elegant silhouettes, UPA style animation, smooth lines, limited color palette (black, red, beige, brown, white), grainy paper texture background, vintage jazz club atmosphere, subtle lighting, slightly exaggerated character proportions, classy and stylish mood.",
  },
  {
    nameKey: "imageStyles.eighties",
    value: "80s",
    imageUrl: "/assets/kitties/80s.webp",
    prompt:
      "Create a selfie styled like a cheesy 1980s mall glamour shot, foggy soft lighting, teal and magenta lasers in the background, feathered hair, shoulder pads, portrait studio vibes, ironic 'glam 4 life' caption.",
  },
  {
    nameKey: "imageStyles.artNouveau",
    value: "art-nouveu",
    imageUrl: "/assets/kitties/art-nouveu.webp",
    prompt:
      "Create an image in an Art Nouveau style: flowing lines, organic shapes, floral motifs, and soft, decorative elegance.",
  },
  {
    nameKey: "imageStyles.synthwave",
    value: "synthwave",
    imageUrl: "/assets/kitties/synthwave.webp",
    prompt:
      "Create an image in a synthwave aesthetic: retro-futuristic 1980s vibe with neon grids, glowing sunset, vibrant magenta-and-cyan gradients, chrome highlights, and a nostalgic outrun atmosphere.",
  },
  {
    nameKey: "imageStyles.sticker",
    value: "sticker",
    imageUrl: "/assets/kitties/sticker.webp",
    prompt:
      "Create a sticker-style image with transparent background: clean vector art, bold outlines, vibrant colors, simplified shapes, and a fun cartoon aesthetic perfect for messaging apps.",
  },
  {
    nameKey: "imageStyles.emoji",
    value: "emoji",
    imageUrl: "/assets/kitties/emoji.webp",
    prompt:
      "Create an emoji-style image: simple, expressive, rounded features, bright colors, minimalist design, clear emotions, and universal visual language suitable for digital communication.",
  },
  {
    nameKey: "imageStyles.studioGhibli",
    value: "studio-ghibli",
    imageUrl: "/assets/kitties/studio-ghibli.webp",
    prompt:
      "Create an image in Studio Ghibli style: soft watercolor textures, whimsical characters, detailed nature elements, warm color palettes, dreamy atmosphere, and magical realism with hand-drawn animation aesthetics.",
  },
  {
    nameKey: "imageStyles.pixelArt",
    value: "pixel-art",
    imageUrl: "/assets/kitties/pixel-art.webp",
    prompt:
      "Create an image in 8-bit pixel art style: retro video game aesthetic, low resolution blocky pixels, simple shading, limited color palette, nostalgic arcade feel, character/environment design suitable for classic NES/SNES games, crisp pixel edges, vibrant but minimal tones, clear silhouettes, iconic game-like atmosphere.",
  },
  {
    nameKey: "imageStyles.kurzgesagt",
    value: "kurzgesagt",
    imageUrl: "/assets/kitties/kurzgesagt.webp",
    prompt:
      "An illustration in the style of Kurzgesagt – In a Nutshell. Use bold, clean vector shapes with flat, vibrant colors and minimal gradients. The composition should feel modern, smooth, and slightly playful, with strong outlines and simple geometry. Lighting should be soft with high contrast between subject and background. Backgrounds should use layered gradients or abstract shapes to create depth. Characters should have large expressive eyes, simple facial features, and exaggerated emotions. The overall aesthetic must be bright, polished, and scientific yet whimsical, like an infographic that feels alive.",
  },
];

/**
 * Built-in translations for image style names
 * Used as fallback when i18n system is not available
 */
export const IMAGE_STYLE_TRANSLATIONS = {
  "imageStyles.cyberpunk": "Cyberpunk",
  "imageStyles.anime": "Anime",
  "imageStyles.dramaticHeadshot": "Dramatic Headshot",
  "imageStyles.coloringBook": "Coloring Book",
  "imageStyles.photoShoot": "Photo Shoot",
  "imageStyles.retroCartoon": "Retro Cartoon",
  "imageStyles.eighties": "80s Glam",
  "imageStyles.artNouveau": "Art Nouveau",
  "imageStyles.synthwave": "Synthwave",
  "imageStyles.sticker": "Sticker Style",
  "imageStyles.emoji": "Emoji Style",
  "imageStyles.studioGhibli": "Studio Ghibli",
  "imageStyles.pixelArt": "8-Bit Pixel Art",
  "imageStyles.kurzgesagt": "Kurzgesagt",
};

/**
 * Helper function to get style name fallback
 * @param {string} nameKey - Translation key
 * @returns {string} Fallback name
 */
export const getStyleFallback = (nameKey) => {
  return IMAGE_STYLE_TRANSLATIONS[nameKey] || nameKey;
};
